import { Component, inject, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { AuthenticationService } from '@core/auth/authentication.service';
import { HasPermissionDirective } from '@shared/directives/has-permission.directive';
import { CombosService } from '../../../../../../shared/services/combos.service';
import { ErrorService } from '../../../../../../shared/services/error.service';
import { SnackbarService } from '@shared/services/snackbar.service';
import { ZonasService } from '../zonas.service';
import { forkJoin } from 'rxjs';
import { Ciudad, Departamento, Pais } from '../../../../../../shared/services/interfaces/combos.interface';
import { FormStatusService } from '../../../../../../shared/services/form-status.service';
import { Card } from '../../../../../../shared/components/common/card/card';
import { LabelComponent } from '../../../../../../shared/components/form/label/label.component';
import { SelectFieldComponent } from '@shared/components/form/select/select-field.component';
import { ToSelectOptionsPipe } from '@shared/pipes/to-select-options.pipe';
import { EditionButtons } from '../../../../../../shared/components/ui/edition-buttons/edition-buttons';
import { Spinner } from '../../../../../../shared/components/ui/spinner/spinner';
import { PageHeaderComponent } from '../../../../../../shared/components/common/page-header/page-header.component';
import { UbicacionService } from '../../ubicacion.service';
import { InputFieldComponent } from '@shared/components/form/input/input-field.component';
import { AppFormComponent } from '@shared/components/ui/list-components';

@Component({
  selector: 'app-editar-zona',
  imports: [
    HasPermissionDirective,
    Card,
    LabelComponent,
    ReactiveFormsModule,
    EditionButtons,
    Spinner,
    PageHeaderComponent,
    SelectFieldComponent,
    ToSelectOptionsPipe,
    InputFieldComponent,
    AppFormComponent,
  ],
  templateUrl: './editar-zona.html',
  styles: ``
})
export class EditarZona implements OnInit {
  readonly permissionAuth = inject(AuthenticationService);

  editForm!: FormGroup;
  loading = false;
  process = false;
  idZona!: number;
  paises: Pais[] = [];
  departamentos: Departamento[] = [];
  ciudades: Ciudad[] = [];
  editable = true;

  private fB = inject(FormBuilder);
  private fS = inject(FormStatusService);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private combosService = inject(CombosService);
  private errorService = inject(ErrorService);
  private snackbarService = inject(SnackbarService);
  private zonasService = inject(ZonasService);
  private ubicacionService = inject(UbicacionService);

  ngOnInit(): void {
    this.idZona = Number(this.route.snapshot.paramMap.get('id'));
    this.makeForm();
    this.getZona();
  }

  private makeForm(): void {
    this.editForm = this.fB.group({
      nombre: [{ value: '', disabled: this.editable }, Validators.required],
      ubi_pais_id: [{ value: '', disabled: this.editable }, Validators.required],
      ubi_departamento_id: [{ value: '', disabled: this.editable }, Validators.required],
      ubi_ciudad_id: [{ value: '', disabled: this.editable }, Validators.required],
    });

    this.editForm.get('ubi_pais_id')?.valueChanges.subscribe(value => {
      if (value) {
        this.getDepartamentos(value);
      }
    });

    this.editForm.get('ubi_departamento_id')?.valueChanges.subscribe(value => {
      if (value) {
        this.getCiudades(value);
      }
    });
  }

  private getDepartamentos(ubi_pais_id: number): void {
    this.combosService.getDepartamentosCombo(ubi_pais_id).subscribe({
      next: (response) => {
        this.departamentos = response.data;
      },
      error: (error) => {
        this.errorService.handleError(error);
      }
    });
  }

  private getCiudades(ubi_departamento_id: number): void {
    this.combosService.getCiudadesCombo(ubi_departamento_id).subscribe({
      next: (response) => {
        this.ciudades = response.data;
      },
      error: (error) => {
        this.errorService.handleError(error);
      }
    });
  }

  getZona(): void {
    this.loading = true;
    this.zonasService.show(this.idZona).subscribe({
      next: (response) => {
        if (response.status) {
          const zona = response.data;

          // Cargar los combos necesarios con los IDs de la zona
          this.loadCascadingCombos(zona.ubi_ciudad_id, zona);
        }
      },
      error: (error) => {
        this.errorService.handleError(error);
      }
    }).add(() => {
      this.loading = false;
    });
  }

  private loadCascadingCombos(ciudadId: number, zona: any): void {
    // Obtener la ciudad con sus relaciones
    this.ubicacionService.getCiudad(ciudadId).subscribe({
      next: (ciudadResponse) => {
        if (ciudadResponse.status && ciudadResponse.data) {
          const ciudad = ciudadResponse.data;
          const departamentoId = ciudad.ubi_departamento_id;

          // Obtener el departamento para saber el país
          this.ubicacionService.getDepartamento(departamentoId).subscribe({
            next: (deptoResponse) => {
              if (deptoResponse.status && deptoResponse.data) {
                const departamento = deptoResponse.data;
                const paisId = departamento.ubi_pais_id;

                // Cargar todos los combos en paralelo
                const paises$ = this.combosService.getPaisesCombo();
                const departamentos$ = this.combosService.getDepartamentosCombo(paisId);
                const ciudades$ = this.combosService.getCiudadesCombo(departamentoId);

                forkJoin([paises$, departamentos$, ciudades$]).subscribe({
                  next: ([paisesRes, deptosRes, ciudadesRes]) => {
                    this.paises = paisesRes.data;
                    this.departamentos = deptosRes.data;
                    this.ciudades = ciudadesRes.data;

                    // Establecer los valores en el formulario
                    this.editForm.patchValue({
                      nombre: zona.nombre,
                      ubi_pais_id: paisId,
                      ubi_departamento_id: departamentoId,
                      ubi_ciudad_id: ciudadId,
                    });
                  },
                  error: (error) => {
                    this.errorService.handleError(error);
                  }
                });
              }
            },
            error: (error) => {
              this.errorService.handleError(error);
            }
          });
        }
      },
      error: (error) => {
        this.errorService.handleError(error);
      }
    });
  }

  updateZona(): void {
    this.process = true;
    const formValue = this.editForm.value;

    const zonaData = {
      nombre: formValue.nombre,
      ubi_ciudad_id: formValue.ubi_ciudad_id,
    };

    this.zonasService.update(this.idZona, zonaData).subscribe({
      next: (zonaResponse) => {
        if (zonaResponse.status) {
          this.snackbarService.showSnackBar('Zona actualizada correctamente');
          this.refreshZona();
        }
      },
      error: (error) => {
        this.errorService.handleError(error);
      }
    }).add(() => {
      this.process = false;
    });
  }

  refreshZona(): void {
    this.getZona();
    this.editableSwitcher();
  }

  editableSwitcher(): void {
    this.fS.changeStatus(this.editForm, this.editable);
    this.editable = !this.editable;
  }

  goBack(): void {
    this.router.navigate(['../'], { relativeTo: this.route });
  }
}
